import { CSSProperties, ChangeEvent, forwardRef, useCallback, useMemo, useState } from "react";

import { LoadingGroup } from "capitalrx-components-2.0/lib/general";
import { Body } from "capitalrx-components-2.0/lib/typography";
import classNames from "classnames";

import { useId } from "../../hooks/useId";
import { getBasePropsFromHtmlProps } from "../../utilities/getBasePropsFromHtmlProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { useInputDisabled, useInputReadonly } from "../InputGroup";
import { useSubmitting } from "../SubmitGroup";
import { useValidity } from "../ValidityGroup";
import { InputContainer } from "../shared";
import { CharacterCount } from "../shared/CharacterCount";
import { normalizeAutocompleteValue } from "../utils/autocompleteHelpers";
import { Overlay } from "./Overlay";
import { Placeholder } from "./Placeholder";
import { StyledNativeInput } from "./StyledNativeInput";
import { TextboxProps } from "./Textbox";

import styles from "./styles.module.scss";

/**
 * The `Input` component properties.
 */
export interface InputProps extends TextboxProps {
  /**
   * Indicates whether the input is required.
   */
  readonly required?: boolean;

  /**
   * The input type.
   */
  readonly type?: "text" | "password";
}

/**
 * The base input for textboxes.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(props, ref) {
  const {
    type = "text",
    label,
    tooltip,
    instructions,
    hideInstructions,
    hideLabel,
    prefix,
    suffix,
    defaultValue = null,
    required = false,
    autoFocus,
    placeholder,
    maxLength,
    tabIndex,
    onChange,
    onFocusEnter,
    onFocusExit,
    autoComplete,
  } = props;

  const { value = defaultValue } = props;

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.Textbox, "textbox"],
    testIdPrefix: "textbox",
  });

  const id = useId();
  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);
  const submitting = useSubmitting();
  const [invalid] = useValidity();

  const [prefixSize, setPrefixSize] = useState<number | null>(null);
  const [suffixSize, setSuffixSize] = useState<number | null>(null);

  const changeHandler = useCallback(
    (ev: ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      if (readOnly || submitting) return;

      onChange?.(ev.target.value, value);
    },
    [value, disabled, readOnly, submitting, onChange],
  );

  const focusHandler = useCallback(() => {
    onFocusEnter?.();
  }, [onFocusEnter]);

  const blurHandler = useCallback(() => {
    onFocusExit?.();
  }, [onFocusExit]);

  const overlayLayoutHandler = useCallback((prefixSize: number | null, suffixSize: number | null) => {
    setPrefixSize(prefixSize);
    setSuffixSize(suffixSize);
  }, []);

  const characterCount = useMemo(() => value?.length, [value]) || 0;

  const autocompleteValue = useMemo(() => normalizeAutocompleteValue(autoComplete), [autoComplete]);

  const style = useMemo(() => {
    if (prefixSize === null && suffixSize === null) return {};
    const result: CSSProperties = {};
    if (prefixSize !== null) result.paddingLeft = `${prefixSize}px`;
    if (suffixSize !== null) result.paddingRight = `${suffixSize}px`;
    return result;
  }, [prefixSize, suffixSize]);

  return (
    <LoadingGroup.Placeholder fallback={<Placeholder className={props.className} style={props.style} hasInstructions={!!instructions} />}>
      <InputContainer
        {...getBasePropsFromHtmlProps(baseHtmlProps)}
        id={id}
        label={label}
        instructions={
          (!!instructions || !!maxLength) && (
            <CharacterCount maxLength={maxLength} characterCount={characterCount}>
              {instructions}
            </CharacterCount>
          )
        }
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        tooltip={tooltip}
        hideInstructions={hideInstructions}
        hideLabel={hideLabel}
      >
        <Body className="input-container">
          <StyledNativeInput
            data-testid={baseHtmlProps["data-testid"]}
            id={id}
            type={type}
            aria-labelledby={!hideLabel ? `${id}-label` : undefined}
            aria-label={hideLabel ? `${type} input ${id}` : undefined}
            aria-invalid={invalid}
            aria-required={required}
            className={classNames("input", { invalid })}
            disabled={disabled}
            autoFocus={autoFocus}
            readOnly={readOnly || submitting}
            aria-readonly={readOnly || submitting}
            aria-busy={submitting}
            aria-describedby={instructions ? `${id}-instructions` : undefined}
            aria-errormessage={invalid ? `${id}-error` : undefined}
            ref={ref}
            tabIndex={disabled || readOnly || submitting ? -1 : (tabIndex ?? 0)}
            placeholder={placeholder}
            style={style}
            value={value ?? ""}
            autoComplete={autocompleteValue}
            onChange={changeHandler}
            onFocus={focusHandler}
            onBlur={blurHandler}
          />
          <Overlay prefix={prefix} suffix={suffix} onLayout={overlayLayoutHandler} />
        </Body>
      </InputContainer>
    </LoadingGroup.Placeholder>
  );
});
